import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  message,
  Spin,
} from "antd";
import {
  BankOutlined,
  DollarOutlined,
  RiseOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import LandingLayout from "./LandingLayout";
import "./BankingFinancials.css";
import apiClient from '../../services/apiClient';

const { Title, Text } = Typography;

const IssuerFinancials = () => {
  const { isDark } = useContext(ThemeContext);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState("annual");
  const [selectedIndustry, setSelectedIndustry] = useState("bank");
  const [financialData, setFinancialData] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";

  // Check if we received data from navigation
  const navigationData = location.state;

  // Handle navigation data - pre-populate if coming from issuer details
useEffect(() => {
  if (navigationData?.selectedSymbol && navigationData?.financialData) {
    setSelectedSymbol(navigationData.selectedSymbol);
    setSelectedIndustry(
      navigationData.financialData.industryType ||
        navigationData.financialData.basicIndustry ||
        "bank"
    );
    setSelectedPeriodType(
      navigationData.financialData.periodType ||
        navigationData.financialData.financialPeriodType ||
        "annual"
    );
    setFinancialData(navigationData.financialData);
    setLoading(false);
  }
}, [navigationData]);

  // Fetch data when params are available
  useEffect(() => {
    if (!selectedSymbol) return;
    fetchIssuerFinancialReport(
      selectedSymbol,
      selectedPeriodType,
      selectedIndustry
    );
  }, [selectedSymbol, selectedPeriodType, selectedIndustry]);

  const fetchIssuerFinancialReport = async (symbol, periodType, industryType) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        symbol,
        periodType,
        industryType,
      });
      const response = await apiClient.get(`${baseUrl}/issuer-financial-reports?${params.toString()}`);
      if (response.status < 200 || response.status >= 300) throw new Error('Failed to fetch issuer financial reports');
      
      const result = response.data;
      if (result.statusCode === 200 && Array.isArray(result.data) && result.data.length > 0) {
        setFinancialData(result.data[0]);
      } else {
        setFinancialData(null);
        message.info('No data available for selected bank');
      }
    } catch (error) {
      console.error('Error fetching banking financials:', error);
      message.error('Failed to load financial data');
      setFinancialData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    const crores = value / 10000000;
    return `₹${crores.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(2)}%`;
  };

  const incomeStatementData = financialData ? [
    { key: '1', item: 'Revenue from Operations', value: formatCurrency(financialData.revenueFromOperations) },
    { key: '2', item: 'Other Income', value: formatCurrency(financialData.otherIncome) },
    { key: '3', item: 'Interest Expended', value: formatCurrency(financialData.interestExpended) },
    { key: '4', item: 'Operating Expenses', value: formatCurrency(financialData.operatingExpenses) },
    { key: '5', item: 'Operating Profit', value: formatCurrency(financialData.operatingProfit) },
    { key: '6', item: 'Provisions & Contingencies', value: formatCurrency(financialData.provisionsAndContingencies) },
    { key: '7', item: 'Profit Before Tax', value: formatCurrency(financialData.profitBeforeTax) },
    { key: '8', item: 'Profit After Tax', value: formatCurrency(financialData.profitAfterTax) },
  ] : [];

  const assetsData = financialData ? [
    { key: '1', item: 'Investments', value: formatCurrency(financialData.assetsInvestments || financialData.investments) },
    { key: '2', item: 'Advances', value: formatCurrency(financialData.assetsAdvances || financialData.advances) },
    { key: '3', item: 'Other Assets', value: formatCurrency(financialData.assetsOther || financialData.otherAssets) },
    { key: '4', item: 'Total Assets', value: formatCurrency(financialData.totalAssets) },
    { key: '5', item: 'Financial Assets', value: formatCurrency(financialData.financialAssets) },
    { key: '6', item: 'Non-Financial Assets', value: formatCurrency(financialData.nonFinancialAssets) },
  ] : [];

  const liabilitiesData = financialData ? [
    { key: '1', item: 'Capital', value: formatCurrency(financialData.liabilitiesCapital || financialData.capital) },
    { key: '2', item: 'Reserves and Surplus', value: formatCurrency(financialData.liabilitiesReservesAndSurplus || financialData.reservesAndSurplus) },
    { key: '3', item: 'Reserves Excl. Revaluation', value: formatCurrency(financialData.liabilitiesReservesExcludingRevaluation) },
    { key: '4', item: 'Deposits', value: formatCurrency(financialData.liabilitiesDeposits || financialData.deposits) },
    { key: '5', item: 'Borrowings', value: formatCurrency(financialData.liabilitiesBorrowings || financialData.borrowings) },
    { key: '6', item: 'Equity', value: formatCurrency(financialData.equity) },
    { key: '7', item: 'Financial Liabilities', value: formatCurrency(financialData.financialLiabilities) },
    { key: '8', item: 'Non-Financial Liabilities', value: formatCurrency(financialData.nonFinancialLiabilities) },
    { key: '9', item: 'Deferred Revenue', value: formatCurrency(financialData.deferredRevenue) },
  ] : [];

  const cashFlowData = financialData ? [
    { key: '1', item: 'Operating Activities', value: formatCurrency(financialData.netCashFromOperating || financialData.netCashFromOperatingActivities) },
    { key: '2', item: 'Investing Activities', value: formatCurrency(financialData.netCashFromInvesting || financialData.netCashFromInvestingActivities) },
    { key: '3', item: 'Financing Activities', value: formatCurrency(financialData.netCashFromFinancing || financialData.netCashFromFinancingActivities) },
    { key: '4', item: 'Cash & Cash Equivalents', value: formatCurrency(financialData.cashAndCashEquivalents) },
    { key: '5', item: 'Closing Cash & Cash Equivalents', value: formatCurrency(financialData.closingCashAndEquivalents || financialData.closingCashAndCashEquivalents) },
    { key: '6', item: 'Net Change in Cash Equivalents', value: formatCurrency(financialData.netChangeInCashEquivalents) },
  ] : [];

  const columns = [
    {
      title: 'Particulars',
      dataIndex: 'item',
      key: 'item',
    },
    {
      title: 'Amount',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
    },
  ];

  return (
    <LandingLayout>
      <div className={`issuer-financials-container ${isDark ? 'dark-mode' : ''}`}>
        <div className="page-header">
          <Title level={2}>
            <BankOutlined /> Issuer Financials
          </Title>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Loading financial data..." />
          </div>
        ) : financialData ? (
          <>
            {/* Key Metrics */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Issuer Name"
                    value={financialData.issuerName || 'N/A'}
                    valueStyle={{ fontSize: '18px' }}
                  />
                  <Text type="secondary">{financialData.symbol}</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Profit After Tax"
                    value={formatCurrency(financialData.profitAfterTax)}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Revenue from Operations"
                    value={formatCurrency(financialData.revenueFromOperations)}
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Ratios */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="CET1 Ratio"
                    value={formatPercentage(financialData.cet1Ratio)}
                    prefix={<PercentageOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Gross NPA %"
                    value={formatPercentage(financialData.grossNpaPercentage)}
                    valueStyle={{ 
                      color: financialData.grossNpaPercentage > 0.05 ? '#cf1322' : '#52c41a' 
                    }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Return on Assets"
                    value={formatPercentage(financialData.returnOnAssets)}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Period"
                    value={financialData.periodEnd}
                    valueStyle={{ fontSize: '16px' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {financialData.auditStatus} • {financialData.periodType?.toUpperCase?.() || ''}
                  </Text>
                </Card>
              </Col>
            </Row>

            {/* Row 1: Income Statement and Cash Flow */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              {/* Income Statement */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Income Statement" 
                  className="financial-card"
                >
                  <Table
                    dataSource={incomeStatementData}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Card>
              </Col>

              {/* Cash Flow */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Cash Flow Statement" 
                  className="financial-card"
                >
                  <Table
                    dataSource={cashFlowData}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Card>
              </Col>
            </Row>

            {/* Row 2: Assets and Liabilities */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              {/* Assets */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Assets" 
                  className="financial-card"
                >
                  <Table
                    dataSource={assetsData}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Card>
              </Col>

              {/* Liabilities */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Liabilities" 
                  className="financial-card"
                >
                  <Table
                    dataSource={liabilitiesData}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Card style={{ marginTop: 24, textAlign: "center", padding: "50px" }}>
            <Text type="secondary">
              No issuer financials to display. Please navigate here from an issuer.
            </Text>
          </Card>
        )}
      </div>
    </LandingLayout>
  );
};

export default IssuerFinancials;

